import { SUBSCRIBER_PLAN } from "./pricingPlans";

export const FREE_TIER_BANDS = [
  {
    id: "tier-1-5",
    label: "1–5 reviews",
    minCount: 1,
    maxCount: 5,
    pricePerReview: 129,
  },
  {
    id: "tier-6-10",
    label: "6–10 reviews",
    minCount: 6,
    maxCount: 10,
    pricePerReview: 89,
  },
  {
    id: "tier-11-plus",
    label: "11+ reviews",
    minCount: 11,
    maxCount: Infinity,
    pricePerReview: 79,
  },
];

const PROGRESS_MAX_REVIEWS = 11;

export function formatEuro(amount) {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(amount);
}

export function resolveFreeTier(reviewCount) {
  if (reviewCount <= 5) return FREE_TIER_BANDS[0];
  if (reviewCount <= 10) return FREE_TIER_BANDS[1];
  return FREE_TIER_BANDS[2];
}

function getFreeTierNudge(reviewCount) {
  if (reviewCount < 6) {
    const needed = 6 - reviewCount;
    return {
      reviewsNeeded: needed,
      message:
        needed === 1
          ? "Add 1 more review to unlock the €89/review package"
          : `Add ${needed} more reviews to unlock the €89/review package`,
      targetTier: FREE_TIER_BANDS[1],
      highlight: true,
    };
  }
  if (reviewCount < 11) {
    const needed = 11 - reviewCount;
    return {
      reviewsNeeded: needed,
      message:
        needed === 1
          ? "Add 1 more review to unlock the €79/review package"
          : `Add ${needed} more reviews to unlock the €79/review package`,
      targetTier: FREE_TIER_BANDS[2],
      highlight: true,
    };
  }
  return {
    reviewsNeeded: 0,
    message: "You’re on our best bulk rate — €79 per review.",
    targetTier: null,
    highlight: false,
  };
}

function getFreeTierProgress(reviewCount) {
  const percent = Math.min(
    100,
    Math.round((reviewCount / PROGRESS_MAX_REVIEWS) * 100)
  );
  const currentTier = reviewCount > 0 ? resolveFreeTier(reviewCount) : FREE_TIER_BANDS[0];
  const milestones = [
    { label: "€129", threshold: 1, percent: (1 / PROGRESS_MAX_REVIEWS) * 100 },
    { label: "€89", threshold: 6, percent: (6 / PROGRESS_MAX_REVIEWS) * 100 },
    { label: "€79", threshold: 11, percent: 100 },
  ];
  return { percent, currentTier, milestones };
}

/** Live quote for Free Tier bulk selection (all reviews billed at current tier rate). */
export function calculateFreeTierQuote(reviewCount) {
  const tier =
    reviewCount > 0 ? resolveFreeTier(reviewCount) : FREE_TIER_BANDS[0];
  const pricePerReview = tier.pricePerReview;
  const total = reviewCount * pricePerReview;
  const nudge = getFreeTierNudge(reviewCount);
  const progress = getFreeTierProgress(reviewCount);

  const baselineTotal = reviewCount * FREE_TIER_BANDS[0].pricePerReview;
  const savingsVsTopTier = Math.max(0, baselineTotal - total);

  return {
    reviewCount,
    tier,
    pricePerReview,
    total,
    nudge,
    progress,
    savingsVsTopTier,
  };
}

/** Live quote for Subscriber selection (monthly sub shown separately). */
export function calculateSubscriberQuote(reviewCount, freeRemaining) {
  const freeApplied = Math.min(reviewCount, Math.max(0, freeRemaining));
  const paidCount = Math.max(0, reviewCount - freeApplied);
  const paidTotal = paidCount * SUBSCRIBER_PLAN.additionalDeletionPrice;

  return {
    reviewCount,
    freeRemaining,
    freeApplied,
    paidCount,
    pricePerPaidReview: SUBSCRIBER_PLAN.additionalDeletionPrice,
    paidTotal,
    caseTotal: paidTotal,
    monthlySubscription: SUBSCRIBER_PLAN.monthlyPrice,
    freeAllowance: SUBSCRIBER_PLAN.freeDeletionsPerMonth,
  };
}
